<template>
  <div>
    <DialogFull
      :data="dialogSettings"
      @close="close"
      :steps="steps"
      @onStepBack="stepBack"
      v-if="dialogSettings.dialog"
      progress
    >
      <template v-slot:dialog.body>
        <div class="my-10 container">
          <v-form ref="form" lazy-validation v-show="steps.current === 1">
            <Select
              autocomplete
              :disabled="isLoading"
              @change="organisationData.parent.id = $event"
              :value="organisationData.parent.id"
              label="Parent Organisation Unit"
              placeholder="Select Organisation Unit"
              :items="units"
              item-value="id"
              item-text="name"
              :rules="isRequired('Parent Organisation Unit', shouldValidate)"
            >
              <template v-slot:selection="{ data }">
                <div class="py-2">
                  <div class="d-flex">
                    <strong v-text="data.item.name" />
                    <strong class="ml-1">
                      {{ data.item.costCode && `(${data.item.costCode})` }}
                    </strong>
                  </div>
                  <p class="secondary-text">{{ data.item.subText }}</p>
                </div>
              </template>
              <template v-slot:item="{ item }">
                <div class="py-2">
                  <div class="d-flex">
                    <strong v-text="item.name" />
                    <strong class="ml-1">
                      {{ item.costCode && `(${item.costCode})` }}
                    </strong>
                  </div>
                  <p class="secondary-text">{{ item.subText }}</p>
                </div>
              </template>
            </Select>
            <label v-if="continueClicked" class="text-red">
              Need to change parent to proceed
            </label>
          </v-form>
          <div class="users" v-if="steps.current == 2">
            <h2>Confirm Changes</h2>
            <p>
              Changing the parent organisation unit can effect who has access to
              it
            </p>
            <OrganisationUnitItem
              toggledByFilter
              viewOnly
              :unit="affectedUnits"
            />
            <div v-if="fetchingUserAccess">
              <v-skeleton-loader type="list-item" v-for="i in 4" :key="i" />
            </div>
            <div v-else>
              <h3 class="mt-2" v-if="usersToGainAccess.length">
                Would gain access to the organisation unit
              </h3>
              <ScopedUserItem
                v-for="user in usersToGainAccess"
                :user="user"
                :key="`gain-${user.id}`"
              />
              <h3 class="mt-2" v-if="usersToLoseAccess.length">
                Would lose access to the organisation unit
              </h3>
              <ScopedUserItem
                v-for="user in usersToLoseAccess"
                :user="user"
                :key="`lose-${user.id}`"
                willLose
              />
            </div>
          </div>
        </div>
      </template>
      <template v-slot:dialog.action>
        <PrimaryButton @click.native="stepForward" v-if="steps.current === 1">
          Continue
        </PrimaryButton>
        <PrimaryButton
          @click.native="handleSubmit"
          :loading="isLoading || fetchingUserAccess"
          v-else
        >
          Confirm Changes
        </PrimaryButton>
      </template>
    </DialogFull>
  </div>
</template>

<script>
import DialogFull from "@/components/common/DialogFull";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import { ORGANISATION_UNITS_NAMESPACE } from "@/store/modules/organisations/organisation-units";
import { createNamespacedHelpers } from "vuex";
import { isRequired } from "@/utils/validation/rules";
import Select from "@/components/common/Select";
import {
  FETCH_SCOPED_USERS,
  FETCH_SCOPED_USERS_PREVIEW,
  EDIT_ORGANISATION_UNIT
} from "@/store/modules/organisations/organisation-units/actions";
import { copyState } from "@/utils/helpers";
import { getUnitsWithParents } from "@/utils/organisation-units";
import ScopedUserItem from "@/views/settings/Organisation/Units/details/ScopedUserItem";
import OrganisationUnitItem from "@/views/settings/Organisation/Units/OrganisationUnitItem";
import { isEmpty, reduce, find } from "lodash";

const { mapActions, mapState } = createNamespacedHelpers(
  ORGANISATION_UNITS_NAMESPACE
);

export default {
  name: "CreateUnitDialog",
  components: {
    Select,
    PrimaryButton,
    DialogFull,
    ScopedUserItem,
    OrganisationUnitItem
  },
  props: {
    unit: Object,
    parent: Object,
    open: Boolean
  },
  created() {
    this.fetchScopedUsers(this.unit.id);
  },
  data() {
    return {
      isDialogOpen: false,
      isLoading: false,
      shouldValidate: false,
      organisationData: {
        parent: {
          id: this.unit && this.unit.parentId
        }
      },
      currentParentId: this.unit && this.unit.parentId,
      dialogSettings: {
        dialog: this.open,
        title: "New Organisation Unit"
      },
      steps: {
        current: 1,
        total: 2
      },
      continueClicked: false
    };
  },
  computed: {
    ...mapState(
      copyState([
        "organisationUnits",
        "scopedUsers",
        "scopedUsersPreview",
        "isFetchingScopedUsers",
        "isFetchingScopedUsersPreview"
      ])
    ),
    isValid() {
      return this.organisationData.parent.id;
    },
    children() {
      return this.unit ? this.unit.organisationUnits : [];
    },
    units() {
      const unitsWithParents = getUnitsWithParents(this.organisationUnits);
      return reduce(
        unitsWithParents,
        (possibleParents, unit) => {
          const child = find(this.children, { id: unit.id });
          if (!child) {
            possibleParents.push(unit);
          }
          return possibleParents;
        },
        []
      );
    },
    affectedUnits() {
      const organisationUnits = [this.unit];
      return !isEmpty(this.parent)
        ? { ...this.parent, organisationUnits }
        : this.unit;
    },
    isParentChange() {
      return this.currentParentId !== this.organisationData.parent.id;
    },
    fetchingUserAccess() {
      return this.isFetchingScopedUsersPreview || this.isFetchingScopedUsers;
    },
    usersToGainAccess() {
      return reduce(
        this.scopedUsersPreview,
        (usersToGain, user) => {
          if (!find(this.scopedUsers, { id: user.id })) {
            usersToGain.push(user);
          }
          return usersToGain;
        },
        []
      );
    },
    usersToLoseAccess() {
      return reduce(
        this.scopedUsers,
        (usersToLose, user) => {
          if (!find(this.scopedUsersPreview, { id: user.id })) {
            usersToLose.push(user);
          }
          return usersToLose;
        },
        []
      );
    }
  },
  methods: {
    ...mapActions({
      fetchScopedUsersPreview: FETCH_SCOPED_USERS_PREVIEW,
      fetchScopedUsers: FETCH_SCOPED_USERS,
      editOrganisationUnit: EDIT_ORGANISATION_UNIT
    }),
    isRequired,
    close() {
      this.$emit("close");
    },
    async handleSubmit() {
      this.isLoading = true;
      await this.editOrganisationUnit({
        organisation: this.organisationData,
        id: this.unit.id
      });
      this.close();
      this.isLoading = false;
    },
    stepBack() {
      if (this.steps.current > 1) {
        this.steps.current--;
      }
    },
    stepForward() {
      this.shouldValidate = true;
      this.$refs.form.validate();
      this.continueClicked = true;
      if (this.isParentChange && this.isValid) {
        this.steps.current++;
        this.getScopedUsers();
        this.continueClicked = false;
      }
    },
    getScopedUsers() {
      const params = {
        parent_organisation_unit_id: this.organisationData.parent.id
      };
      this.fetchScopedUsersPreview({ id: this.unit.id, params });
    }
  }
};
</script>

<style scoped>
p {
  margin-bottom: 0 !important;
}
</style>
